import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config';
import SearchableSelect from './SearchableSelect';

/**
 * ResourcePicker — live-lookup dropdown fed by
 *   GET /credentials/{credential_id}/resources/{resource_type}[?parent=...]
 *
 * Used to replace free-text ID fields (Slack channel_id, Notion database_id,
 * HubSpot pipeline, Jira project_key, etc.) with a searchable dropdown of
 * real options fetched from the remote API using the stored credential.
 *
 * Props:
 *   value          — current selected option value (string)
 *   onChange       — (newValue: string) => void
 *   credentialId   — the credential_id used to authenticate the remote call
 *   resourceType   — backend handler key (e.g. 'slack_channels', 'notion_databases')
 *   parent         — optional parent id for cascading lookups (e.g. pipeline → stages)
 *   placeholder    — text shown when nothing is selected
 *   style          — passed through to SearchableSelect
 *   disabled       — boolean
 */
export default function ResourcePicker({
  value,
  onChange,
  credentialId,
  resourceType,
  parent,
  placeholder,
  style = {},
  disabled = false,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const lastFetchKey = useRef(null);

  const fetchOptions = useCallback(async ({ force = false } = {}) => {
    if (!credentialId || !resourceType) {
      setOptions([]);
      setError('');
      return;
    }
    const key = `${credentialId}|${resourceType}|${parent || ''}`;
    if (!force && lastFetchKey.current === key && options.length > 0) return;
    lastFetchKey.current = key;
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token') || '';
      const qs = parent ? `?parent=${encodeURIComponent(parent)}` : '';
      const resp = await fetch(
        `${API_BASE_URL}/credentials/${credentialId}/resources/${resourceType}${qs}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      setOptions(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load');
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [credentialId, resourceType, parent, options.length]);

  useEffect(() => {
    fetchOptions();
  }, [credentialId, resourceType, parent]);  // eslint-disable-line react-hooks/exhaustive-deps

  const disabledNow = disabled || !credentialId;
  const effectivePlaceholder = !credentialId
    ? '-- Select a credential first --'
    : loading
    ? '-- Loading --'
    : error
    ? `-- Error: ${error} --`
    : options.length === 0
    ? '-- No items found --'
    : placeholder || '-- Select --';

  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'stretch', marginBottom: '15px' }}>
      <div style={{ flex: 1 }}>
        <SearchableSelect
          value={value || ''}
          onChange={onChange}
          options={options}
          placeholder={effectivePlaceholder}
          style={{ ...style, marginBottom: 0 }}
          disabled={disabledNow}
        />
      </div>
      <button
        type="button"
        onClick={() => fetchOptions({ force: true })}
        disabled={disabledNow || loading}
        title="Reload options from the service"
        style={{
          padding: '0 10px',
          background: 'var(--bg-tertiary)',
          border: '1px solid var(--border-color)',
          color: 'var(--text-primary)',
          borderRadius: '4px',
          cursor: disabledNow || loading ? 'not-allowed' : 'pointer',
          fontSize: '14px',
          opacity: disabledNow || loading ? 0.6 : 1,
        }}
      >
        {loading ? '…' : '↻'}
      </button>
    </div>
  );
}
